// Centralized API utility for all API calls

const API_BASE_URL = 'https://leadapi.crmbytes.com/api';
const GEOLOCATION_API_URL = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object>} Geolocation data
 */
export const fetchGeolocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API_URL, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    if (!response.ok) {
      throw new Error('Failed to fetch geolocation');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Geolocation API error:', error);
    return null;
  }
};

/**
 * Submit lead data to the API
 * @param {Object} leadData - Lead information
 * @returns {Promise<Object>} API response
 */
export const submitLead = async (leadData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/Lead/AddLead`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'accept': 'text/plain'
      },
      body: JSON.stringify(leadData)
    });

    const data = await response.json();
    
    return {
      success: response.ok,
      statusCode: response.status,
      data: data
    };
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      statusCode: 500,
      data: {
        errorMessage: 'Network error. Please try again.',
        message: 'Network error. Please try again.'
      }
    };
  }
};

/**
 * Prepare lead payload from form data and geolocation
 * @param {Object} formData - Form data from user input
 * @param {Object} geolocationData - Geolocation data from API
 * @returns {Object} Formatted lead payload
 */
export const prepareLeadPayload = (formData, geolocationData) => {
  // Extract numeric dial code from countryCode (e.g., "+1" -> 1)
  const dialCode = formData.countryCode 
    ? parseInt(formData.countryCode.replace('+', '')) 
    : 1;

  const payload = {
    name: formData.name || '',
    email: formData.email || '',
    countryDialCode: dialCode,
    country: formData.country || 'US',
    phoneNumber: formData.contactNumber || '', // Only numeric, no dial code
    ipAddress: geolocationData?.data?.ipAddress || '',
    description: formData.message || '',
    pageUrl: window.location.href,
    city: geolocationData?.data?.cityName || '',
    state: geolocationData?.data?.regionName || '',
    geoLocation: geolocationData ? JSON.stringify(geolocationData) : '',
    packageTitle: formData.packageTitle || '',
    packageAmount: formData.packageAmount || 0
  };

  return payload;
};

/**
 * Handle API response and show appropriate messages
 * @param {Object} response - API response
 * @returns {Object} Processed response with user-friendly message
 */
export const handleApiResponse = (response) => {
  const { success, statusCode, data } = response;

  if (statusCode === 200 && data.isSuccess) {
    return {
      success: true,
      customerId: data.data?.customerId,
      message: data.data?.message || 'Lead submitted successfully'
    };
  }

  if (statusCode === 401) {
    return {
      success: false,
      message: data.message || 'Unauthorized access'
    };
  }

  if (statusCode === 400) {
    return {
      success: false,
      message: data.errorMessage || data.data?.message || 'Invalid data submitted'
    };
  }

  return {
    success: false,
    message: data.errorMessage || 'An error occurred. Please try again.'
  };
};
